/*
================================================================================
| FILENAME: src/services/apiService.js (Updated for Refresh Logic)
| PURPOSE:  To add a 'forceRefresh' parameter to the HomeView data fetching
|           functions, allowing the UI to bypass the cache. This is the complete
|           and merged version of the file.
================================================================================
*/
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://martechai-backend.onrender.com';

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
});

const apiService = {
    // --- Auth & User ---
    login: (email, password) => apiClient.post('/auth/login', { email, password }).then(res => res.data),
    register: (name, email, password) => apiClient.post('/auth/register', { name, email, password }).then(res => res.data),
    fetchUser: () => apiClient.get(`/auth/user?timestamp=${new Date().getTime()}`).then(res => res.data.user || null),
    logout: () => apiClient.post('/auth/logout').then(res => res.data),
    getGoogleLoginUrl: () => `${API_BASE_URL}/auth/google/login`,
    getGoogleConnectUrl: (service) => `${API_BASE_URL}/auth/google/connect?service=${service}`,
    updateUserProfile: (userId, profileData) => apiClient.put(`/api/users/${userId}/profile`, profileData).then(res => res.data),
    updatePassword: (newPassword, confirmPassword) => apiClient.put('/api/account/password', { newPassword, confirmPassword }).then(res => res.data),

    // --- Content Creator / Cargo Bay ---
    uploadContentAsset: (file) => {
        const formData = new FormData();
        formData.append('asset', file);
        return apiClient.post('/api/foundry/content-asset', formData).then(res => res.data);
    },
    publishAsset: (assetData) => apiClient.post('/api/foundry/publish-asset', assetData).then(res => res.data),
    fetchPublishedAssets: () => apiClient.get('/api/foundry/published-assets').then(res => res.data),
    updatePublishedAsset: (assetId, assetData) => apiClient.put(`/api/foundry/published-assets/${assetId}`, assetData).then(res => res.data),
    deletePublishedAsset: (assetId) => apiClient.delete(`/api/foundry/published-assets/${assetId}`).then(res => res.data),
    optimizeContent: (payload) => apiClient.post('/api/foundry/optimize-content', payload).then(res => res.data),
    
    // --- Workspaces & Connections ---
    createWorkspace: (name) => apiClient.post('/api/workspaces', { name }).then(res => res.data),
    fetchWorkspaces: () => apiClient.get('/api/workspaces').then(res => res.data),
    switchWorkspace: (workspaceId) => apiClient.post('/api/workspaces/switch', { workspaceId }).then(res => res.data),
    deleteWorkspace: (workspaceId) => apiClient.delete(`/api/workspaces/${workspaceId}`).then(res => res.data),
    fetchConnections: () => apiClient.get('/api/connections').then(res => res.data),
    disconnectConnection: (serviceId) => apiClient.delete(`/api/connections/${serviceId}`).then(res => res.data),
    uploadWorkspaceIcon: (workspaceId, file) => {
        const formData = new FormData();
        formData.append('icon', file);
        return apiClient.post(`/api/workspaces/${workspaceId}/icon`, formData).then(res => res.data);
    },

    // --- Google Services ---
    fetchAnalyticsAccounts: () => apiClient.get('/api/analytics-accounts').then(res => res.data),
    saveGaProperty: (propertyId) => apiClient.post('/api/workspaces/save-ga-property', { propertyId }).then(res => res.data),
    fetchSearchConsoleSites: () => apiClient.get('/api/search-console/sites').then(res => res.data),
    saveSearchConsoleSite: (siteUrl) => apiClient.post('/api/search-console/sites', { siteUrl }).then(res => res.data),
    fetchAdWordsCustomers: () => apiClient.get('/api/adwords/customers').then(res => res.data),
    saveAdWordsCustomer: (customerId) => apiClient.post('/api/adwords/customers', { customerId }).then(res => res.data),
    testGaConnection: () => apiClient.get('/api/analytics-accounts/test-query').then(res => res.data),
    testScConnection: () => apiClient.get('/api/search-console/test-query').then(res => res.data),
    testAdsConnection: () => apiClient.get('/api/adwords/test-query').then(res => res.data),
    
    // --- HubSpot ---
    testHsConnection: () => apiClient.get('/api/hubspot/test-connection').then(res => res.data),
    getHubspotConnectUrl: () => `${API_BASE_URL}/auth/hubspot/login`,
    executeHubspotQuery: (query) => apiClient.post('/api/hubspot/query', query).then(res => res.data),
    expandHubspotTitles: (buyingCommittee) => apiClient.post('/api/hubspot/expand-titles', { buyingCommittee }).then(res => res.data),
    createHubspotTargetTable: (payload) => apiClient.post('/api/hubspot/create-target-account-table', payload).then(res => res.data),

    // --- AI Chat ---
    sendChatMessage: (payload) => apiClient.post('/api/ai/chat', payload).then(res => res.data),
    fetchChatHistory: () => apiClient.get('/api/ai/chat/latest').then(res => res.data),

    // --- WordPress ---
    connectWordPress: (connectData) => apiClient.post('/api/wordpress/connect', connectData).then(res => res.data),
    syncWordPressContent: () => apiClient.post('/api/wordpress/sync').then(res => res.data),
    fetchWpCategories: () => apiClient.get('/api/wordpress/categories').then(res => res.data),
    createWpCategoryTable: (tableName, categoryIds) => apiClient.post('/api/wordpress/create-category-table', { tableName, categoryIds }).then(res => res.data),
    
    // --- Connections & Integrations ---
    saveGoodDaySettings: (payload) => apiClient.post('/api/connections/goodday/save', payload).then(res => res.data),
    // Generic disconnect (works for all services, including goodday)
    disconnectIntegration: (serviceId) => apiClient.delete(`/api/connections/${serviceId}`),
    disconnectService: (serviceId, opts = {}) => {
    const preferPost = ['goodday', 'wordpress', 'hubspot'];
    const method = opts.method || (preferPost.includes(serviceId) ? 'POST' : 'DELETE');

    if (method === 'POST') {
        return apiClient.post(`/api/connections/${serviceId}/disconnect`).then(res => res.data);
    }
    return apiClient.delete(`/api/connections/${serviceId}`).then(res => res.data);
},

    // --- Data Hub ---
    fetchDataTables: () => apiClient.get('/api/datatables').then(res => res.data),
    createDataTable: (name, source, aiQuery) => apiClient.post('/api/datatables', { name, source, aiQuery }).then(res => res.data),
    deleteDataTable: (tableId) => apiClient.delete(`/api/datatables/${tableId}`).then(res => res.data),
    fetchDataTableData: (tableId) => apiClient.get(`/api/datatables/${tableId}/data`).then(res => res.data),
    analyzeTableContent: (tableId) => apiClient.post(`/api/datatables/analyze-content/${tableId}`).then(res => res.data),
    setCtaSource: (tableId) => apiClient.post(`/api/datatables/set-cta-source/${tableId}`).then(res => res.data),
    createDataTableFromFile: (name, file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name);
        return apiClient.post('/api/datatables/upload-csv', formData).then(res => res.data);
    },
    overwriteDataTable: (tableId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post(`/api/datatables/overwrite/${tableId}`, formData).then(res => res.data);
    },

    // --- SEO Audit ---
    startSiteWideAudit: (name, sitemapUrl = null, auditType = 'regular') => apiClient.post('/api/seo-audit', { name, sitemapUrl, auditType }).then(res => res.data),
    fetchAuditHistory: () => apiClient.get('/api/seo-audit').then(res => res.data),
    fetchAuditReport: (reportId) => apiClient.get(`/api/seo-audit/${reportId}`).then(res => res.data),
    deleteAuditReport: (reportId) => apiClient.delete(`/api/seo-audit/${reportId}`).then(res => res.data),
    executeSeoFix: (fixData) => apiClient.post('/api/seo-audit/execute-fix', fixData).then(res => res.data),

    // --- Competitor Analysis ---
    runCompetitorAnalysis: (competitorUrl, productId) => apiClient.post('/api/foundry/competitor-analysis', { competitorUrl, productId }).then(res => res.data),
    fetchCompetitorAnalysisHistory: () => apiClient.get('/api/foundry/competitor-analysis').then(res => res.data),
    fetchCompetitorAnalysisReport: (reportId) => apiClient.get(`/api/foundry/competitor-analysis/${reportId}`).then(res => res.data),
    deleteCompetitorAnalysisReport: (reportId) => apiClient.delete(`/api/foundry/competitor-analysis/${reportId}`).then(res => res.data),
   
    // --- Compass / Brand Hub ---
    fetchBrandHub: () => apiClient.get('/api/brand-hub').then(res => res.data),
    updateBrandHub: (updateData) => apiClient.put('/api/brand-hub', updateData).then(res => res.data),
    createCustomerProfile: (profileData) => apiClient.post('/api/brand-hub/customer-profiles', profileData).then(res => res.data),
    updateCustomerProfile: (profileId, profileData) => apiClient.put(`/api/brand-hub/customer-profiles/${profileId}`, profileData).then(res => res.data),
    deleteCustomerProfile: (profileId) => apiClient.delete(`/api/brand-hub/customer-profiles/${profileId}`).then(res => res.data),
    updateProduct: (productId, productData) => apiClient.put(`/api/brand-hub/products/${productId}`, productData).then(res => res.data),
    deleteProduct: (productId) => apiClient.delete(`/api/brand-hub/products/${productId}`).then(res => res.data),
    generateProductKeywords: (productId) => apiClient.post(`/api/brand-hub/products/${productId}/generate-keywords`).then(res => res.data),
    uploadProductLogo: (productId, file) => {
        const formData = new FormData();
        formData.append('logo', file);
        return apiClient.post(`/api/brand-hub/assets/product/${productId}/logo`, formData).then(res => res.data);
    },
    uploadBrandAsset: (file) => {
        const formData = new FormData();
        formData.append('asset', file);
        return apiClient.post('/api/brand-hub/assets/brand', formData).then(res => res.data);
    },
    deleteBrandAsset: (assetId) => apiClient.delete(`/api/brand-hub/assets/brand/${assetId}`).then(res => res.data),
    uploadProductAsset: (productId, file) => {
        const formData = new FormData();
        formData.append('asset', file);
        return apiClient.post(`/api/brand-hub/assets/product/${productId}`, formData).then(res => res.data);
    },
    deleteProductAsset: (productId, assetId) => apiClient.delete(`/api/brand-hub/assets/product/${productId}/${assetId}`).then(res => res.data),

    // --- NEW: n8n Processing ---
    processProductAssets: (productId) => apiClient.post('/api/n8n/process-product-assets', { productId }).then(res => res.data),
// --- Content Creator ---
    optimizeContent: (payload) => apiClient.post('/api/foundry/optimize-content', payload).then(res => res.data),


    // --- Campaigns ---
    fetchCampaigns: () => apiClient.get('/api/campaigns').then(res => res.data),
    fetchCampaign: (campaignId) => apiClient.get(`/api/campaigns/${campaignId}`).then(res => res.data),
    createCampaign: (campaignData) => apiClient.post('/api/campaigns', campaignData).then(res => res.data),
    updateCampaign: (campaignId, updateData) => apiClient.put(`/api/campaigns/${campaignId}`, updateData).then(res => res.data),
    deleteCampaign: (campaignId) => apiClient.delete(`/api/campaigns/${campaignId}`).then(res => res.data),
    deployCampaign: (campaignId) => apiClient.post(`/api/campaigns/${campaignId}/deploy`).then(res => res.data),
    generateCampaignInsights: (payload) => apiClient.post('/api/campaigns/generate-insights', payload).then(res => res.data),
    generateCampaignGoalSuggestion: (payload) => apiClient.post('/api/campaigns/generate-goal-suggestion', payload).then(res => res.data),
    generateBuyingCommittee: (payload) => apiClient.post('/api/campaigns/generate-buying-committee', payload).then(res => res.data),
    generateAITimeline: (data) => apiClient.post('/api/campaigns/generate-timeline', data).then(res => res.data),
    generateAllCampaignContent: (payload) => apiClient.post(`/api/campaigns/generate-all-content`, payload).then(res => res.data),
    deployCampaignToGoodDay: (campaignId) => apiClient.post(`/api/campaigns/${campaignId}/deploy-to-goodday`).then(res => res.data),

    // --- Analytics Hub ---
    fetchDashboards: () => apiClient.get('/api/dashboards').then(res => res.data),
    addDashboard: (dashboardData) => apiClient.post('/api/dashboards', dashboardData).then(res => res.data),
    updateDashboard: (dashboardId, updateData) => apiClient.put(`/api/dashboards/${dashboardId}`, updateData).then(res => res.data),
    deleteDashboard: (dashboardId) => apiClient.delete(`/api/dashboards/${dashboardId}`).then(res => res.data),
    fetchWidgetData: (payload) => apiClient.post('/api/analytics/data/widget', payload).then(res => res.data),
    fetchWidgetData: (payload) => apiClient.post('/api/analytics-accounts/data/widget', payload).then(res => res.data),
    fetchIntegrationFields: (integration) => apiClient.get(`/api/analytics/fields/${integration}`).then(res => res.data),
    
    // --- Flight Deck ---
    fetchFlightDeckTasks: () => apiClient.get('/api/flightdeck').then(res => res.data),
    createFlightDeckTask: (taskData) => apiClient.post('/api/flightdeck', taskData).then(res => res.data),
    updateFlightDeckTask: (taskId, taskData) => apiClient.put(`/api/flightdeck/${taskId}`, taskData).then(res => res.data),
    deleteFlightDeckTask: (taskId) => apiClient.delete(`/api/flightdeck/${taskId}`).then(res => res.data),

    // --- Team ---
    fetchTeamMembers: () => apiClient.get('/api/team/members').then(res => res.data),
    inviteUser: (userData) => apiClient.post('/api/team/members', userData).then(res => res.data),
    updateUser: (userId, updateData) => apiClient.put(`/api/team/members/${userId}`, updateData).then(res => res.data),
    deleteUser: (userId) => apiClient.delete(`/api/team/members/${userId}`).then(res => res.data),
    
    // --- Idea Hangar ---
    pinIdea: (ideaData) => apiClient.post('/api/foundry/ideas/pin', ideaData).then(res => res.data),

    // --- HomeView Dashboard Widget Functions (with Refresh Logic) ---
    fetchSeoOpportunities: (limit = 3, forceRefresh = false) => {
        return apiClient.get(`/api/foundry/home/seo-opportunities`, { params: { limit, forceRefresh } }).then(res => res.data);
    },
    fetchCompetitorWatchFeed: (limit = 3, forceRefresh = false) => {
        return apiClient.get(`/api/foundry/home/competitor-watch`, { params: { limit, forceRefresh } }).then(res => res.data);
    },
    fetchNewIdeas: (limit = 3, forceRefresh = false) => {
        return apiClient.get(`/api/foundry/home/new-ideas`, { params: { limit, forceRefresh } }).then(res => res.data);
    },
    fetchAdwordsInsights: (limit = 3, forceRefresh = false) => {
        return apiClient.get('/api/foundry/home/adwords-insights', { params: { limit, forceRefresh } }).then(res => res.data);
    },
};

export default apiService;